/**
 * Typed wrapper for the AmDistributor (anonymity mining) contract.
 *
 * Storage layout MUST match `am_distributor.tolk :: AmDistributorStorage`.
 *
 * Off-chain claim flow:
 *   1. User derives the AM nullifier hash from their deposit's
 *      `nullifier` field via `Poseidon(nullifier, AM_DOMAIN_TAG)` —
 *      distinct from the withdraw nullifier hash. See
 *      `computeAmNullifierHash` below.
 *   2. User generates a Groth16 proof against `amClaim_final.zkey`
 *      (produced by ceremony; see TOKENOMICS.md anonymity-mining section).
 *   3. User sends `Claim(root, amNullifierHash, recipient, relayer,
 *      fee, refund, proof)` to the distributor. Distributor verifies
 *      and pays out `denomination × amRateBps / 10000` TONADO to
 *      recipient (minus relayer fee).
 *
 * Public-signal order (MUST match amClaim.circom :: main):
 *   [root, amNullifierHash, recipient, relayer, fee, refund]
 */
import { Address, beginCell, SendMode, toNano, } from "@ton/core";
import { JETTON_BURN_ADDRESS } from "./tonadoJetton";
import { serialiseGroth16Proof } from "./pool";
export const AM_DISTRIBUTOR_OPS = {
    CLAIM: 0xded06005,
    ADMIN_BIND_JETTON_WALLET: 0xded06001,
    ADMIN_SET_AM_RATE: 0xded06002,
    ADMIN_ADD_ROOT: 0xded06003,
    ADMIN_REMOVE_ROOT: 0xded06004,
    ADMIN_PAUSE: 0xded057f1,
    ADMIN_UNPAUSE: 0xded057f2,
    LOG_AM_CLAIM: 0xded060a1,
};
/**
 * AM domain tag — MUST match `am_distributor.tolk :: AM_DOMAIN_TAG` and
 * the `AM_DOMAIN_TAG` constant baked into `amClaim.circom`.
 * Changing this value invalidates the ceremony and bricks every
 * already-claimed nullifier mapping. Treat as immutable post-deploy.
 *
 * Current value: eight repetitions of 0xDEADC0DE (224 bits). Above the
 * 248-bit max of any randomly-sampled depositor secret yet well below
 * the BLS12-381 scalar field modulus r ≈ 2^254.9.
 */
export const AM_DOMAIN_TAG = BigInt("0xDEADC0DEDEADC0DEDEADC0DEDEADC0DEDEADC0DEDEADC0DEDEADC0DE");
/**
 * Compute the AM nullifier hash from a deposit's `nullifier` field.
 * Uses Poseidon(2)(nullifier, AM_DOMAIN_TAG) — distinct from the
 * withdraw nullifier hash which uses Poseidon(1)(nullifier).
 *
 * Requires a Poseidon instance from `buildPoseidon()` in this package
 * (already used by the deposit/withdraw flows).
 */
export async function computeAmNullifierHash(nullifier, buildPoseidon) {
    const { poseidon } = await buildPoseidon();
    return poseidon([nullifier, AM_DOMAIN_TAG]);
}
export class AmDistributor {
    address;
    init;
    constructor(address, init) {
        this.address = address;
        this.init = init;
    }
    static at(address) {
        return new AmDistributor(address);
    }
    static BURN_ADDRESS = JETTON_BURN_ADDRESS;
    /**
     * Build initial data.
     *
     * Storage (inline):
     *   bool      paused
     *   address   owner
     *   coins     denomination
     *   uint16    amRateBps
     *   coins     availableForClaims    (tracked funding balance)
     *   Maybe ref knownRoots (dict<uint256, slice1>)
     *   Maybe ref amNullifiers (dict<uint256, slice1>)
     *   ref       config → AmConfig { tonadoMaster, distributorJettonWallet, pool }
     */
    static buildInitialData(opts) {
        const config = beginCell()
            .storeAddress(opts.tonadoMaster)
            .storeAddress(opts.distributorJettonWallet ?? JETTON_BURN_ADDRESS)
            .storeAddress(opts.pool)
            .endCell();
        return beginCell()
            .storeUint(opts.paused ? 1 : 0, 1)
            .storeAddress(opts.owner)
            .storeCoins(opts.denomination)
            .storeUint(opts.amRateBps, 16)
            .storeCoins(opts.availableForClaims ?? 0n)
            .storeUint(0, 1) // knownRoots = null
            .storeUint(0, 1) // amNullifiers = null
            .storeRef(config)
            .endCell();
    }
    static fromInit(opts) {
        const data = AmDistributor.buildInitialData(opts);
        const stateInitCell = beginCell()
            .storeUint(0, 2)
            .storeUint(3, 2)
            .storeRef(opts.code)
            .storeRef(data)
            .storeUint(0, 1)
            .endCell();
        return new AmDistributor(new Address(opts.workchain ?? 0, stateInitCell.hash()), { code: opts.code, data });
    }
    // ---- Admin ops ----
    async sendAdminBindJettonWallet(provider, via, opts) {
        const body = beginCell()
            .storeUint(AM_DISTRIBUTOR_OPS.ADMIN_BIND_JETTON_WALLET, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeAddress(opts.walletAddress)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminSetAmRate(provider, via, opts) {
        const body = beginCell()
            .storeUint(AM_DISTRIBUTOR_OPS.ADMIN_SET_AM_RATE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.amRateBps, 16)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminAddRoot(provider, via, opts) {
        const body = beginCell()
            .storeUint(AM_DISTRIBUTOR_OPS.ADMIN_ADD_ROOT, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.root, 256)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminRemoveRoot(provider, via, opts) {
        const body = beginCell()
            .storeUint(AM_DISTRIBUTOR_OPS.ADMIN_REMOVE_ROOT, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.root, 256)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminPause(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(AM_DISTRIBUTOR_OPS.ADMIN_PAUSE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    async sendAdminUnpause(provider, via, opts = {}) {
        const body = beginCell()
            .storeUint(AM_DISTRIBUTOR_OPS.ADMIN_UNPAUSE, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.05"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    // ---- Claim ----
    /**
     * Submit an AM claim. Mirrors pool Withdraw shape.
     */
    async sendClaim(provider, via, opts) {
        const proofCell = await serialiseGroth16Proof(opts.proof);
        const coreCell = beginCell()
            .storeAddress(opts.recipient)
            .storeAddress(opts.relayer)
            .storeCoins(opts.fee)
            .storeCoins(opts.refund)
            .endCell();
        const body = beginCell()
            .storeUint(AM_DISTRIBUTOR_OPS.CLAIM, 32)
            .storeUint(opts.queryId ?? 0n, 64)
            .storeUint(opts.root, 256)
            .storeUint(opts.amNullifierHash, 256)
            .storeRef(coreCell)
            .storeRef(proofCell)
            .endCell();
        await provider.internal(via, {
            value: opts.value ?? toNano("0.4"),
            sendMode: SendMode.PAY_GAS_SEPARATELY,
            body,
        });
    }
    // ---- Get methods ----
    async getDenomination(provider) {
        const res = await provider.get("getDenomination", []);
        return res.stack.readBigNumber();
    }
    async getAmRateBps(provider) {
        const res = await provider.get("getAmRateBps", []);
        return res.stack.readNumber();
    }
    async getMaxAmRateBps(provider) {
        const res = await provider.get("getMaxAmRateBps", []);
        return res.stack.readNumber();
    }
    async getAmReward(provider) {
        const res = await provider.get("getAmReward", []);
        return res.stack.readBigNumber();
    }
    async getTonadoMaster(provider) {
        const res = await provider.get("getTonadoMaster", []);
        return res.stack.readAddress();
    }
    async getDistributorJettonWallet(provider) {
        const res = await provider.get("getDistributorJettonWallet", []);
        return res.stack.readAddress();
    }
    async getPool(provider) {
        const res = await provider.get("getPool", []);
        return res.stack.readAddress();
    }
    async getAmDomainTag(provider) {
        const res = await provider.get("getAmDomainTag", []);
        return res.stack.readBigNumber();
    }
    async isKnownRoot(provider, root) {
        const res = await provider.get("isKnownRoot", [
            { type: "int", value: root },
        ]);
        return res.stack.readNumber() !== 0;
    }
    async isAmNullifierClaimed(provider, amNullifierHash) {
        const res = await provider.get("isAmNullifierClaimed", [
            { type: "int", value: amNullifierHash },
        ]);
        return res.stack.readNumber() !== 0;
    }
    async isPaused(provider) {
        const res = await provider.get("isPaused", []);
        return res.stack.readNumber() !== 0;
    }
    // Tracked funding balance reader.
    async getAvailableForClaims(provider) {
        const res = await provider.get("getAvailableForClaims", []);
        return res.stack.readBigNumber();
    }
}
//# sourceMappingURL=amDistributor.js.map